import { Card } from "../../../components/ui/card";
import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Navigate, useNavigate } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../../utils/toast-utils";
import { EyeOff, Eye } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { doAdminLogin } from "../../../services/admin/adminAuthService";
import Loader from "../../../components/loader/loader";
import useAdminProfile from "../../../store/admin/profile";
import { getRequest } from "../../../config/axiosConfig";

const AdminLogin = () => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [checkAuthLoading, setCheckAuthLoading] = useState(true);
  const navigate = useNavigate();

  const { setProfileData } = useAdminProfile();

  const [formData, setFormData] = useState({
    phone: "",
    password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {

      if (!/^\d*$/.test(value) || value.length > 10) return;

      setFormData((prev) => ({ ...prev, phone: value }));

      setErrors((prev) => ({
        ...prev,
        phone:
          value.length !== 10 ? "Phone number must be exactly 10 digits" : "",
      }));

      return;
    }

    if (name === "password") {
      const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

      setFormData((prev) => ({ ...prev, password: value }));

      setErrors((prev) => ({
        ...prev,
        password: passwordRegex.test(value)
          ? ""
          : "Min 8 chars, include uppercase, lowercase, number & special character",
      }));
      return;
    }
  };

  // const sendOTP = () => {
  //   showSuccessToast("Login Success");
  //   navigate("/admin-dashboard");
  // };

  // const verifyOTP = () => {

  //   showSuccessToast("OTP Verify");
  //   navigate("/admin-dashboard");
  // };

  // const forgotPassWord = () => {
  //   showSuccessToast("OTP Send");
  //   setStep("otp");
  // };


  const handleLogin = async () => {

    if (!formData.phone || !formData.password) {
      return showErrorToast("please enter details");
    }

    if (formData.phone.length !== 10) {
      return showErrorToast("Phone number must be 10 digits");
    }

    setIsLoading(true);

    try {

      const payload = {
        phone: formData.phone,
        password: formData.password,
      };

      const res = await doAdminLogin(payload);

      if (res) { setProfileData(res?.data?.data) }

      const status = res?.data?.status || res?.data;

      if (status === 200) {

        showSuccessToast(
          res?.data?.message || res?.message || "Login Successfully",
        );
        navigate("/admin-dashboard");

      } else {

        showErrorToast(res?.message || res?.data?.message || res?.data?.data?.message);

      }
    } catch (error) {

      showErrorToast(error?.response?.data?.message || "Something went wrong");
      setIsLoading(false);

    } finally {
      setIsLoading(false);
    }
  };

  const home = () => {
    navigate("/");
  };

  useEffect(() => {
    const checkAdminAuth = async () => {
      try {
        const res = await getRequest({
          url: process.env.REACT_APP_BASE_URL + "auth/admin/me-admin",
        });

        if (res?.success) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.log(error);
        setIsAuthenticated(false);
      } finally {
        setCheckAuthLoading(false);
      }
    };

    checkAdminAuth();
  }, []);

  if (checkAuthLoading) return <Loader />;

  if (isAuthenticated) {
    return <Navigate to="/admin-dashboard" replace />;
  }

  return (
    <>
      <div className="bg-[#081a3a]">
        {isLoading && <Loader />}
        <div>
          <Button onClick={home} variant="admin.primary" className="ml-10 mt-2">
            Back To Home
          </Button>
        </div>
        <div className="min-h-[calc(100vh-48px)]  bg-gradient-to-r bg-[#081a3a] flex items-center justify-center">
          <Card className="w-full md:w-[100vh] min-h-[33.33vh] border-[#081a3a] ">
            <div className="md:flex md:flex-row w-full rounded-xl">
              <div
                className="w-full md:w-2/5 min-h-[30vh] md:min-h-[33.33vh]
                                    bg-[#0a1f44] md:rounded-l-2xl flex items-center"
              >
                <div className="text-white w-full items-center text-center md:px-14 md:items-start md:text-left">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    Admin Login
                  </h2>

                  <p className="text-sm sm:text-md mb-5">
                    Secure access to your health services
                  </p>

                  <ul className="text-sm sm:text-md space-y-2">
                    <li>✔ Use registered Mobile Number</li>
                    <li>✔ Keep your password confidential</li>
                    <li>✔ OTP will be required for verification</li>
                    <li>✔ Never share OTP with anyone</li>
                  </ul>
                </div>
              </div>

              <div className="rounded-b-2xl sm:rounded-b-none  md:rounded-r-2xl md:w-3/5 min-h-[33.33vh] p-12 bg-white text-black">
                {step === "login" && (
                  <div>
                    <h3 className="text-2xl font-bold mb-4">Sign In</h3>
                    <div>
                      <Input
                        type="number"
                        name="phone"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full border p-2 mb-3 rounded"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mb-2">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                    <div className="relative mb-4">
                      <Input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full border p-2 mb-4 rounded"
                      />
                      {errors.password && (
                        <p className="text-red-500 text-sm mb-2">
                          {errors.password}
                        </p>
                      )}

                      <Button
                        variant="admin.default"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-[1px] -translate-y-1/2 text-sm text-blue-600 top-[22px] px-2"
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </Button>
                    </div>

                    <Button
                      onClick={handleLogin}
                      disabled={formData?.phone?.length !== 10 || !formData.password || errors.password || isLoading}
                      variant="admin.secondary"
                      className="w-full text-xl font-bold"
                    >
                      Login
                    </Button>

                    {/* <div className="mt-3 text-sm text-right ">
                      <Button
                        onClick={() => setStep("forgot")}
                        variant="admin.link"
                      >
                        Forgot Password?
                      </Button>
                    </div> */}
                  </div>
                )}

                {/* STEP 2: OTP */}
                {/* {step === "otp" && (
                  <div>
                    <h3 className="text-2xl font-bold mb-4">
                      Two-Factor Authentication
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Enter OTP sent to your registered Phone
                    </p> */}
                {/* <div>
                      <Input
                        type="number"
                        name="otp"
                        placeholder="Enter 6-digit OTP"
                        value={formData.otp}
                        onChange={handleChange}
                        className="w-full border p-2 mb-2 rounded"
                      />

                      {errors.otp && (
                        <p className="text-red-500 text-sm">{errors.otp}</p>
                      )}
                    </div> */}

                {/* <Button
                      onClick={verifyOTP}
                      disabled={formData.otp.length !== 6}
                      className={`w-full p-2 mt-2 rounded ${
                        formData.otp.length === 6
                          ? "bg-green-600 text-white cursor-pointer"
                          : "bg-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Verify OTP
                    </Button> */}

                {/* <p className="mt-3 text-sm text-center">
                      Didn’t receive OTP?{" "}
                      <Button variant="admin.link">Resend</Button>
                    </p>
                  </div>
                )} */}

                {/* STEP 3: FORGOT PASSWORD */}
                {/* {step === "forgot" && (
                  <div>
                    <h3 className="text-2xl font-bold mb-4">Reset Password</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      OTP will be sent to registered Phone
                    </p>

                    <div>
                      <Input
                        type="number"
                        name="username"
                        placeholder="Phone Number"
                        value={form.username}
                        onChange={handleChange}
                        className="w-full border p-2 mb-3 rounded"
                      />
                      {errors.username && (
                        <p className="text-red-500 text-sm mb-2">
                          {errors.username}
                        </p>
                      )}
                    </div>

                    <Button
                      disabled={form?.username?.length !== 10}
                      // onClick={forgotPassWord}
                      variant="admin.secondary"
                      className="w-full text-xl font-bold"
                    >
                      Send OTP
                    </Button>

                    <div className="mt-3 text-sm text-right">
                      <Button
                        onClick={() => setStep("login")}
                        variant="admin.link"
                      >
                        Back to Login
                      </Button>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
